// Pure helpers for persisting the Dispatch Planner GridStack layout.
//
// The hazard these guard against: collapsing a card shrinks its GridStack
// height to 1 (header only). If that h=1 is written into the saved layout, a
// later reload renders the card one grid row tall even though its collapsed
// UI state isn't persisted — so it looks "expanded but super short" and the
// only recovery is to unlock and drag it back. Dispatch Dashboard already
// guards/heals against this; these helpers give the Planner the same safety.

export type GridPos = { x: number; y: number; w: number; h: number };

// The leg-type flags the backend computes per order (any leg of that type).
export type OrderLegTypeFlags = {
  hasPickup?: boolean;
  hasDelivery?: boolean;
  hasTerminate?: boolean;
};

/**
 * Predicate for the Dispatch Planner's Pick Up / Delivery / Terminate / Other
 * quick-filter buttons (restored from the old planner's per-card toolbar).
 *
 * With no buttons active, every order matches. When one or more are active the
 * selected types are OR-ed: an order matches if it has ANY selected type.
 * "other" matches orders with none of pickup/delivery/terminate.
 */
export function orderMatchesLegTypes(
  order: OrderLegTypeFlags,
  activeFilters: string[],
): boolean {
  if (activeFilters.length === 0) return true;
  if (activeFilters.includes('pickup') && order.hasPickup) return true;
  if (activeFilters.includes('delivery') && order.hasDelivery) return true;
  if (activeFilters.includes('terminate') && order.hasTerminate) return true;
  if (
    activeFilters.includes('other') &&
    !order.hasPickup &&
    !order.hasDelivery &&
    !order.hasTerminate
  ) {
    return true;
  }
  return false;
}

// A GridStack height this small means the card is collapsed (header only),
// not a real expanded size worth persisting.
const COLLAPSED_H = 1;

/**
 * Merge the live grid positions (`next`) into the saved layout (`prev`).
 * A collapsed height (h <= 1) never overwrites a card's stored expanded
 * height — the previous height is kept so the next reload/expand restores
 * the size the user actually chose.
 */
export function mergeGridLayout(
  prev: Record<string, GridPos>,
  next: Record<string, GridPos>,
): Record<string, GridPos> {
  const merged: Record<string, GridPos> = { ...prev };
  for (const id of Object.keys(next)) {
    const pos = next[id];
    const prevH = prev[id]?.h;
    const h = pos.h <= COLLAPSED_H && prevH && prevH > COLLAPSED_H ? prevH : pos.h;
    merged[id] = { ...pos, h };
  }
  return merged;
}

/**
 * Read the saved per-card collapsed flags from a parsed layout payload,
 * falling back to `defaults` (all expanded) for anything missing. Lets the
 * collapsed/expanded state of each card survive a reload.
 */
export function readSavedCollapsed<K extends string>(
  saved: { collapsed?: Partial<Record<K, boolean>> } | null | undefined,
  defaults: Record<K, boolean>,
): Record<K, boolean> {
  return { ...defaults, ...(saved?.collapsed ?? {}) };
}

/**
 * Heal a layout loaded from storage: any card persisted at the collapsed
 * height (h < 2) by an older build is restored to its default expanded
 * height so it doesn't render header-only after a reload.
 */
export function healLayoutHeights(
  layout: Record<string, GridPos>,
  defaults: Record<string, GridPos>,
): Record<string, GridPos> {
  const healed: Record<string, GridPos> = {};
  for (const key of Object.keys(layout)) {
    const pos = layout[key];
    healed[key] =
      pos && pos.h < 2 && defaults[key] ? { ...pos, h: defaults[key].h } : pos;
  }
  return healed;
}

// Move types the Imports endpoint returns that belong on the Planner's
// Domestic card rather than the Imports card. IMPORT and REPO stay on Imports.
const DOMESTIC_MOVE_TYPES = new Set(['DOMESTIC', 'INBOUND', 'OUTBOUND']);

export function isDomesticPlannerMove(moveType: string | null | undefined): boolean {
  return DOMESTIC_MOVE_TYPES.has((moveType ?? '').toUpperCase());
}

/**
 * Split the single `/orders/imports` response into the rows the Imports card
 * shows and the rows the Domestic card shows. The endpoint already returns
 * DOMESTIC / INBOUND / OUTBOUND alongside IMPORT and REPO, so the Domestic
 * card is a client-side partition, not a second fetch. Anything with an
 * unknown or missing move type stays on Imports.
 */
export function partitionPlannerImports<T extends { moveType?: string | null }>(
  orders: T[],
): { importOrders: T[]; domesticOrders: T[] } {
  const importOrders: T[] = [];
  const domesticOrders: T[] = [];
  for (const order of orders) {
    (isDomesticPlannerMove(order.moveType) ? domesticOrders : importOrders).push(order);
  }
  return { importOrders, domesticOrders };
}

/**
 * Add any card present in `defaults` but missing from a saved layout. A new
 * card is appended below the bottom edge of every saved card (keeping its
 * default x / w / h) so it never lands on top of a position the user chose.
 * With nothing saved, the defaults are returned as-is.
 */
export function placeMissingCards(
  saved: Record<string, GridPos>,
  defaults: Record<string, GridPos>,
): Record<string, GridPos> {
  const savedKeys = Object.keys(saved);
  if (savedKeys.length === 0) return { ...defaults };
  const placed: Record<string, GridPos> = { ...saved };
  let bottom = savedKeys.reduce((max, k) => Math.max(max, saved[k].y + saved[k].h), 0);
  for (const key of Object.keys(defaults)) {
    if (placed[key]) continue;
    placed[key] = { ...defaults[key], y: bottom };
    bottom += defaults[key].h;
  }
  return placed;
}
